import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Tag as TagIcon, Plus, Trash2, ExternalLink, MessageSquare, Calendar, CheckSquare, StickyNote, Clock, GitBranch, CreditCard, User as UserIcon,
  Zap, AlertCircle, CheckCircle2, Hourglass, ShieldAlert, ShieldCheck, Mail, Phone, FileText, Receipt,
  Printer,
} from "lucide-react";
import { Badge, Button, Input, Label, Modal, ModalFooter, Select, Textarea, useToast, useConfirm } from "@/components/ui";
import { TaskDialog } from "@/features/tasks/TaskDialog";
import { AppointmentFormModal } from "@/features/scheduling/AppointmentFormModal";
import { PanelErrorRow } from "./PanelErrorRow";
import { usePanelData } from "./usePanelData";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateTime, formatMoney, formatRelative } from "@/lib/format";
import type {
  Activity, Appointment, Contact, ContactAutomations, ContactConsentRow, FormRequest, Invoice, Note, Opportunity, Payment, Product, Tag, Task,
  Service,
  Provider,
} from "@/lib/types";

/**
 * Self-contained right-rail panels shared by the contact-detail page and
 * the standalone inbox. Each panel takes a `contactId` and fetches its
 * own data; that's slower than the contact-detail page's old
 * Promise.all-everything pattern but eliminates the parent prop-drilling
 * mess and lets the inbox embed any subset of these panels without
 * reimplementing them.
 *
 * Panels that need bidirectional state (tags, notes, tasks) take a
 * minimal `onChange`-style callback so the parent can react to mutations
 * (e.g. the contact-detail header reflects updated tag membership).
 */

// ─── Timeline ─────────────────────────────────────────────────────────────
const ACTIVITY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "contact.created": Plus,
  "contact.updated": UserIcon,
  "contact.tag_added": TagIcon,
  "message.sent": MessageSquare,
  "message.received": MessageSquare,
  "appointment.booked": Calendar,
  "appointment.completed": Calendar,
  "appointment.cancelled": Calendar,
  /* Moved to another chair so somebody's treatment could be added - the
     client is not told, but their record says it happened. */
  "appointment.reseated": Calendar,
  "task.created": CheckSquare,
  "task.completed": CheckSquare,
  "note.created": StickyNote,
  "form.submitted": ExternalLink,
  "opportunity.created": GitBranch,
  "opportunity.stage_changed": GitBranch,
  "opportunity.won": GitBranch,
  "opportunity.lost": GitBranch,
  "automation.started": Zap,
  "automation.finished": CheckCircle2,
  "automation.failed": AlertCircle,
};

/**
 * Tone per activity type — drives both the icon color and the bubble
 * background tint. Positive outcomes = emerald, negative = rose, info /
 * neutral activity = indigo or zinc. Anything not explicitly mapped
 * falls back to zinc.
 */
const ACTIVITY_TONES: Record<string, { icon: string; bg: string }> = {
  "contact.created":          { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "contact.tag_added":        { icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "message.sent":             { icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "message.received":         { icon: "text-sky-300",      bg: "bg-sky-500/10" },
  "appointment.booked":       { icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "appointment.completed":    { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "appointment.cancelled":    { icon: "text-rose-300",     bg: "bg-rose-500/10" },
  "appointment.reseated":     { icon: "text-zinc-300",     bg: "bg-zinc-500/10" },
  "task.created":             { icon: "text-amber-300",    bg: "bg-amber-500/10" },
  "task.completed":           { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "note.created":             { icon: "text-amber-300",    bg: "bg-amber-500/10" },
  "form.submitted":           { icon: "text-sky-300",      bg: "bg-sky-500/10" },
  "opportunity.created":      { icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "opportunity.stage_changed":{ icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "opportunity.won":          { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "opportunity.lost":         { icon: "text-rose-300",     bg: "bg-rose-500/10" },
  "automation.started":       { icon: "text-indigo-300",   bg: "bg-indigo-500/10" },
  "automation.finished":      { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "automation.failed":        { icon: "text-rose-300",     bg: "bg-rose-500/10" },
};
const DEFAULT_TONE = { icon: "text-zinc-400", bg: "bg-zinc-800" };

export function TimelinePanel({
  contactId,
  max = 25,
  opsOnly = false,
}: {
  contactId: string;
  max?: number;
  /** Drop message/note chatter — those already live in the Messages tab, and
   *  repeating them here buried the operational history she needs. */
  opsOnly?: boolean;
}) {
  const { data: activities, error, reload } = usePanelData(
    () => api.get<{ activities: Activity[] }>("/activities", { contact_id: contactId, limit: max })
      .then((d) => opsOnly
        ? d.activities.filter((a) => !a.type.startsWith("message.") && a.type !== "note.created")
        : d.activities),
    [contactId, max, opsOnly]
  );

  return (
    <Card
      icon={Clock}
      title="Timeline"
      count={activities?.length}
    >
      <div className="max-h-72 overflow-y-auto p-3">
        {error ? (
          <PanelErrorRow message={error} onRetry={reload} />
        ) : activities === null ? (
          <p className="text-xs text-zinc-500 text-center py-4">Loading…</p>
        ) : activities.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-4">No activity yet</p>
        ) : (
          <ul className="space-y-2.5">
            {activities.slice(0, max).map((a) => {
              const Icon = ACTIVITY_ICONS[a.type] ?? Clock;
              const tone = ACTIVITY_TONES[a.type] ?? DEFAULT_TONE;
              const isAutomation = a.type.startsWith("automation.");
              const automationId = isAutomation ? (a.data?.automation_id as string | undefined) : undefined;
              const body = (
                <>
                  <div className={"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full " + tone.bg}>
                    <Icon className={"h-3.5 w-3.5 " + tone.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200">{a.summary || a.type}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{formatRelative(a.occurred_at)}</p>
                  </div>
                </>
              );
              return (
                <li key={a.id}>
                  {automationId ? (
                    <Link to={`/automations/${automationId}`} className="flex items-start gap-3 rounded -m-1 p-1 hover:bg-zinc-800/40">
                      {body}
                    </Link>
                  ) : (
                    <div className="flex items-start gap-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── Tasks ────────────────────────────────────────────────────────────────
/**
 * Open tasks for a contact. Self-contained: New-task button opens an
 * embedded create dialog; clicking a task row opens it for editing
 * (same dialog in edit mode — no separate modal). The component is
 * intentionally identical wherever it renders — contact-detail page,
 * inbox info rail, anywhere else — so behaviour stays in lockstep.
 */
export function TasksPanel({
  contactId, max = 8,
}: {
  contactId: string;
  max?: number;
}) {
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  // null = closed, "new" = create form, Task = edit form. One shape both
  // for opening fresh and reopening to edit.
  const [editing, setEditing] = useState<Task | "new" | null>(null);

  const reload = () => {
    let cancelled = false;
    api.get<{ tasks: Task[] }>("/tasks", { contact_id: contactId, limit: max })
      .then((d) => { if (!cancelled) setTasks(d.tasks); })
      .catch(() => { if (!cancelled) setTasks([]); });
    return () => { cancelled = true; };
  };
  useEffect(() => reload(), [contactId, max]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (t: Task) => {
    if (!tasks) return;
    const next = t.status === "completed" ? "pending" : "completed";
    setTasks((prev) => prev ? prev.map((x) => x.id === t.id
      ? { ...x, status: next, completed_at: next === "completed" ? new Date().toISOString() : null }
      : x,
    ) : prev);
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setTasks((prev) => prev ? prev.map((x) => x.id === t.id ? t : x) : prev);
    }
  };

  return (
    <Card
      icon={CheckSquare}
      title="Tasks"
      action={<PlusButton onClick={() => setEditing("new")} aria="New task" />}
    >
      {tasks === null ? (
        <p className="text-xs text-zinc-500 px-4 py-3">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-zinc-500 px-4 py-3">No tasks</p>
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {tasks.slice(0, max).map((t) => (
            <li key={t.id} className="flex items-start gap-2 px-4 py-2">
              <input
                type="checkbox"
                checked={t.status === "completed"}
                onChange={() => toggle(t)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
              />
              <button
                type="button"
                onClick={() => setEditing(t)}
                className="flex-1 min-w-0 text-left rounded -m-1 p-1 hover:bg-zinc-800/40"
              >
                <p className={"text-sm " + (t.status === "completed" ? "line-through text-zinc-500" : "text-zinc-200")}>{t.title}</p>
                {t.due_at && <p className="text-[10px] text-zinc-500 mt-0.5">{formatDateTime(t.due_at)}</p>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <TaskDialog
        target={editing}
        defaultContactId={contactId}
        onClose={() => setEditing(null)}
        onSaved={(saved, mode) => {
          setTasks((prev) => {
            if (!prev) return [saved];
            if (mode === "create") return [saved, ...prev];
            return prev.map((x) => x.id === saved.id ? saved : x);
          });
          setEditing(null);
        }}
        onDeleted={(deleted) => {
          setTasks((prev) => prev ? prev.filter((x) => x.id !== deleted.id) : prev);
          setEditing(null);
        }}
      />
    </Card>
  );
}

// ─── Appointments ─────────────────────────────────────────────────────────
/**
 * A client's visits.
 *
 * Two of her asks land here: "I'll need the arrangement of history of
 * appointments to be top is the newest to bottom latest", and "I also need a
 * tab for upcoming appointments … like a section in overview for upcoming
 * appointments?". So the panel takes a side (`when`), the history reads
 * newest first, and each row can carry the paperwork for that visit — "is it
 * possible to have a link to the forms needed for that appointment".
 */
export function AppointmentsPanel({
  contactId, max = 5, when, title, withForms = false,
}: {
  contactId: string;
  max?: number;
  /** Nothing = every visit. "upcoming" = today onwards, oldest first. */
  when?: "upcoming" | "past";
  title?: string;
  withForms?: boolean;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [refs, setRefs] = useState<{ services: Service[]; providers: Provider[] }>({ services: [], providers: [] });
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState<string | null>(null);

  /** Send an outstanding form to the client again. */
  const resend = async (f: FormRequest) => {
    setResending(f.id);
    try {
      await api.post(`/forms/requests/${f.id}/resend`);
      toast.success(`Sent "${f.template_name ?? "the form"}" again`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't resend that");
    } finally {
      setResending(null);
    }
  };

  const { data, error, reload } = usePanelData(
    async () => {
      const appts = await api
        .get<{ appointments: Appointment[] }>("/appointments", {
          contact_id: contactId, limit: max,
          // A cancelled visit is off the calendar, but it is still part of
          // this client's history and the desk needs to see it happened.
          include_cancelled: "1",
          ...(when ? { when } : { order: "desc" }),
        })
        .then((d) => d.appointments);
      if (!withForms || appts.length === 0) return { appts, forms: [] as FormRequest[] };
      const forms = await api
        .get<{ requests: FormRequest[] }>("/forms/requests", { contact_id: contactId, limit: 200 })
        .then((d) => d.requests)
        .catch(() => [] as FormRequest[]);
      return { appts, forms };
    },
    [contactId, max, when, withForms]
  );

  const heading = title ?? (when === "upcoming" ? "Coming up" : "Appointments");

  /**
   * "in overview in chart upcoming visits can we be able to click on that to
   *  cancel or reschedule"
   *
   * A visit that has not happened yet opens the booking form it came from, so
   * the time can be moved or the whole thing called off without hunting for
   * the person on the calendar. A past visit is history and stays read-only.
   */
  const openVisit = async (a: Appointment) => {
    if (busy) return;
    setBusy(true);
    try {
      const [sv, pv] = await Promise.all([
        api.get<{ services: Service[] }>("/services"),
        api.get<{ providers: Provider[] }>("/providers"),
      ]);
      setRefs({ services: sv.services.filter((x) => !x.archived), providers: pv.providers });
      setEditing(a);
    } catch {
      toast.error("Couldn't open that visit. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  // Compact panel: hide entirely when there's no data and no error.
  // Surface a small error row if the fetch actually failed so the user
  // knows to retry instead of silently assuming "no appointments."
  if (error) {
    return (
      <Card icon={Calendar} title={heading}>
        <PanelErrorRow message={error} onRetry={reload} />
      </Card>
    );
  }
  if (!data || data.appts.length === 0) return null;
  const byAppointment = new Map<string, FormRequest[]>();
  for (const f of data.forms) {
    if (!f.appointment_id) continue;
    const list = byAppointment.get(f.appointment_id) ?? [];
    list.push(f);
    byAppointment.set(f.appointment_id, list);
  }

  return (
    <Card icon={Calendar} title={heading}>
      {editing && (
        <AppointmentFormModal
          mode={{ kind: "reschedule", appointment: editing }}
          services={refs.services}
          providers={refs.providers}
          defaultDate={editing.starts_at.slice(0, 10)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      <ul className="divide-y divide-zinc-800/40">
        {data.appts.slice(0, max).map((a) => {
          const forms = byAppointment.get(a.id) ?? [];
          const canOpen = when === "upcoming" && a.status !== "cancelled" && a.status !== "completed";
          return (
            <li key={a.id} data-visit-row className="px-4 py-2.5">
              {/* "if it can show the visit in complete what they had or what
                  they are scheduled for" — the treatments on the visit itself,
                  not the title typed when it was booked, and whether it
                  happened. */}
              {canOpen ? (
                <button
                  type="button"
                  onClick={() => void openVisit(a)}
                  disabled={busy}
                  className="block w-full truncate text-left text-sm text-zinc-200 hover:text-indigo-300 disabled:opacity-60"
                  title="Open this visit to change the time or cancel it"
                >
                  {(a.segments ?? []).map((sg) => sg.service_name).filter(Boolean).join(" + ") || a.title}
                </button>
              ) : (
                <p className="truncate text-sm text-zinc-200">
                  {(a.segments ?? []).map((sg) => sg.service_name).filter(Boolean).join(" + ") || a.title}
                </p>
              )}
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
                {formatDateTime(a.starts_at)}
                <VisitStatus status={a.status} />
                {/* "listed in each other chart under appointment and if I
                    click in appointment I can get to the receipt where it is
                    clear who received what services" — when a family is rung
                    up together the sale sits in the payer's order history, so
                    this is how everybody else reaches it. */}
                {a.invoice_id && (
                  <Link
                    to={`/register?order=${a.invoice_id}`}
                    className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200"
                    title="Open the receipt this visit was paid on"
                  >
                    <Receipt className="h-3 w-3" />
                    {a.invoice_number ?? "Receipt"}
                  </Link>
                )}
              </p>
              {forms.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {forms.map((f) => (
                    <li key={f.id} data-visit-form className="flex items-center gap-1.5 text-[11px]">
                      <FileText className="h-3 w-3 shrink-0 text-zinc-600" />
                      <Link
                        to={`/contacts/${contactId}?tab=forms`}
                        className="truncate text-zinc-400 hover:text-indigo-300"
                      >
                        {f.template_name ?? "Form"}
                      </Link>
                      <span className={cn(
                        "shrink-0 rounded px-1 text-[10px]",
                        f.status === "completed" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300",
                      )}>
                        {f.status === "completed" ? "done" : "not done"}
                      </span>
                      {/* "can we have a button in the upcoming appointment
                          with option to resend paperwork to be completed" —
                          on the form that is actually outstanding, so nobody
                          has to work out which one to chase. */}
                      {f.status !== "completed" && (
                        <button
                          type="button"
                          onClick={() => void resend(f)}
                          disabled={resending === f.id}
                          className="shrink-0 rounded px-1 text-[10px] text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-50"
                          title="Send this one to them again"
                        >
                          {resending === f.id ? "sending…" : "resend"}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ─── Opportunities ────────────────────────────────────────────────────────
export function OpportunitiesPanel({ contactId, max = 5 }: { contactId: string; max?: number }) {
  const { data: opportunities, error, reload } = usePanelData(
    () => api.get<{ opportunities: Opportunity[] }>("/opportunities", { contact_id: contactId, limit: max }).then((d) => d.opportunities),
    [contactId, max]
  );

  if (error) {
    return (
      <Card icon={GitBranch} title="Opportunities">
        <PanelErrorRow message={error} onRetry={reload} />
      </Card>
    );
  }
  if (!opportunities || opportunities.length === 0) return null;
  return (
    <Card icon={GitBranch} title="Opportunities">
      <ul className="divide-y divide-zinc-800/40">
        {opportunities.slice(0, max).map((o) => (
          <li key={o.id} className="px-4 py-2.5">
            <Link to={`/pipelines?opportunity=${o.id}`} className="text-sm text-zinc-200 hover:text-indigo-300 truncate block">{o.title}</Link>
            <p className="text-xs text-zinc-500 mt-0.5 capitalize">{o.stage_id} · ${((o.value_cents ?? 0) / 100).toLocaleString()}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────
/**
 * Payment activity for a contact: paid + open totals plus a list of
 * recent invoices. Self-contained: the New-invoice button opens an
 * embedded create dialog. Renders identically wherever it's used —
 * the contact-detail page and the inbox info rail share the exact
 * same component.
 */
export function PaymentsPanel({ contactId }: { contactId: string }) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewing, setViewing] = useState<Invoice | null>(null);

  const reload = () => {
    let cancelled = false;
    // Per-contact panels show at most a handful of recent rows — cap the
    // fetch so a high-volume contact doesn't pull 100s of rows into a
    // sidebar that only renders the first 5–10.
    Promise.all([
      api.get<{ invoices: Invoice[] }>("/payments/invoices", { contact_id: contactId, limit: 20 }).catch(() => ({ invoices: [] })),
      api.get<{ payments: Payment[] }>("/payments", { contact_id: contactId, limit: 20 }).catch(() => ({ payments: [] })),
    ]).then(([inv, pay]) => {
      if (cancelled) return;
      setInvoices(inv.invoices);
      setPayments(pay.payments);
    });
    return () => { cancelled = true; };
  };
  useEffect(() => reload(), [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPaid = (payments ?? [])
    .filter((p) => p.status === "succeeded")
    .reduce((s, p) => s + p.amount_cents - p.refunded_amount_cents, 0);
  const openTotal = (invoices ?? [])
    .filter((i) => i.status === "open" || i.status === "uncollectible")
    .reduce((s, i) => s + (i.total_cents - i.amount_paid_cents), 0);

  return (
    <Card
      icon={CreditCard}
      title="Payments"
      action={<PlusButton onClick={() => setCreateOpen(true)} aria="New invoice" />}
    >
      <div className="grid grid-cols-2 gap-px border-b border-zinc-800/40 bg-zinc-800/40 text-center">
        <div className="bg-zinc-900/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total paid</p>
          <p className="text-sm font-semibold text-emerald-400 mt-0.5 tabular-nums">{formatMoney(totalPaid)}</p>
        </div>
        <div className="bg-zinc-900/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Open</p>
          <p className={"text-sm font-semibold mt-0.5 tabular-nums " + (openTotal > 0 ? "text-amber-400" : "text-zinc-500")}>
            {formatMoney(openTotal)}
          </p>
        </div>
      </div>
      {(invoices === null || payments === null) ? (
        <p className="text-xs text-zinc-500 px-4 py-3">Loading…</p>
      ) : invoices.length === 0 && payments.length === 0 ? (
        <p className="text-xs text-zinc-500 px-4 py-3">No payments yet</p>
      ) : (
        <ul className="divide-y divide-zinc-800/40 max-h-72 overflow-y-auto">
          {/* "I need to be able to click on the invoice review and/or print" -
              the rows used to be dead unless the invoice happened to have a
              hosted page, which a till sale never does. */}
          {invoices.slice(0, 5).map((inv) => (
            <li key={inv.id}>
              <button
                type="button"
                onClick={() => setViewing(inv)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-xs hover:bg-zinc-900/40"
              >
                <div className="min-w-0">
                  <p className="truncate text-zinc-200">Invoice {inv.number ?? inv.id.slice(0, 8)}</p>
                  <p className="text-[10px] text-zinc-500">{formatRelative(inv.created_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge color={inv.status === "paid" ? "emerald" : inv.status === "open" ? "indigo" : "zinc"} size="xs">{inv.status}</Badge>
                  <span className="font-medium tabular-nums text-zinc-200">{formatMoney(inv.total_cents, inv.currency)}</span>
                  {inv.hosted_url && (
                    <a
                      href={inv.hosted_url} target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-zinc-500 hover:text-indigo-300" aria-label="Open the hosted page"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <InvoiceCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contactId={contactId}
        onCreated={() => { setCreateOpen(false); reload(); }}
      />
      {viewing && (
        <InvoiceDetailModal
          invoice={viewing}
          onClose={() => setViewing(null)}
          onCancelled={() => { setViewing(null); reload(); }}
        />
      )}
    </Card>
  );
}

/**
 * How a package on the ticket reaches them.
 *
 * "for Maureen bagshaw I must have wrong her up wrong because she took all
 *  her injections home but when o look at the invoice it does not tell me can
 *  we have invoice show take home or in lounge or mail out so we know"
 *
 * The till already records this per line; the receipt just never showed it.
 */
const FULFILMENT: Record<string, { label: string; color: string }> = {
  take_home: { label: "Taken home", color: "teal" },
  mail_out: { label: "Posted out", color: "sky" },
  in_lounge: { label: "In the lounge", color: "zinc" },
};

/** How the ticket was settled, in her words rather than ours. */
const PAID_BY: Record<string, string> = {
  cash: "Cash",
  card_external: "Card on the card machine",
  card_on_file: "The card on file",
  card_link: "A payment link",
  none: "Nothing owed - closed out",
};

/**
 * One ticket, in full, with a Print button.
 *
 * "we need to know why the invoice in software shows paid and I need to be
 *  able to click on the invoice review and/or print"
 *
 * So it says what was on it, what came off, and how it was settled - that
 * last line is the one that answers "why does this say paid": pressing
 * "Card, terminal" at the till records the sale as paid on her card machine.
 */
function InvoiceDetailModal({ invoice, onClose, onCancelled }: {
  invoice: Invoice; onClose: () => void; onCancelled: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [cancelling, setCancelling] = useState(false);
  const lines = invoice.line_items ?? [];
  const method = String((invoice.metadata as Record<string, unknown> | null)?.payment_method ?? "");
  const qtyOf = (li: typeof lines[number]) => li.qty ?? li.quantity ?? 1;
  const subtotal = lines.reduce((t, li) => t + li.unit_amount_cents * qtyOf(li), 0);

  return (
    <Modal open onClose={onClose} title={`Invoice ${invoice.number ?? invoice.id.slice(0, 8)}`} size="md">
      <div id="invoice-print" className="space-y-3 text-sm">
        <div className="flex items-baseline justify-between gap-3 text-xs text-zinc-500">
          <span>{formatDateTime(invoice.paid_at ?? invoice.created_at)}</span>
          <Badge color={invoice.status === "paid" ? "emerald" : "amber"} size="xs">{invoice.status}</Badge>
        </div>
        {lines.length === 0 ? (
          <p className="text-xs text-zinc-500">No lines on this ticket.</p>
        ) : (
          <ul className="divide-y divide-zinc-800/40 rounded-lg border border-zinc-800">
            {lines.map((li, i) => (
              <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-zinc-200">
                    {qtyOf(li) > 1 && <span className="text-zinc-500">{qtyOf(li)} × </span>}
                    {li.name}
                  </span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {li.kind && <span className="text-[11px] uppercase tracking-wide text-zinc-600">{li.kind}</span>}
                    {(() => {
                      const meta = (li.meta ?? {}) as Record<string, unknown>;
                      const key = typeof meta.fulfilment === "string"
                        ? meta.fulfilment
                        : meta.take_home === true ? "take_home" : null;
                      const f = key ? FULFILMENT[key] : null;
                      return f ? <Badge color={f.color} size="xs">{f.label}</Badge> : null;
                    })()}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block tabular-nums text-zinc-300">
                    {formatMoney(li.unit_amount_cents * qtyOf(li), invoice.currency)}
                  </span>
                  {/* "so when they get receipt they will see percentage off or
                      free or partial discount ... as well as why" */}
                  {(() => {
                    const meta = (li.meta ?? {}) as Record<string, unknown>;
                    const off = Number(meta.discount_cents ?? 0);
                    if (!off) return null;
                    const was = Number(meta.list_amount_cents ?? 0) * qtyOf(li);
                    const why = typeof meta.discount_reason === "string" ? meta.discount_reason : "";
                    return (
                      <span className="block text-[11px] text-amber-300">
                        {was > 0 && <span className="text-zinc-500 line-through">{formatMoney(was, invoice.currency)}</span>}
                        {was > 0 ? " " : ""}-{formatMoney(off, invoice.currency)}{why ? ` · ${why}` : ""}
                      </span>
                    );
                  })()}
                </span>
              </li>
            ))}
          </ul>
        )}
        <dl className="space-y-1 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-zinc-500">Subtotal</dt>
            <dd className="tabular-nums text-zinc-300">{formatMoney(subtotal, invoice.currency)}</dd>
          </div>
          {invoice.discount_cents > 0 && (
            <div className="flex justify-between gap-3">
              {/* "whatever the reason for the discount I need to see in the
                  invoice so if we go back it needs to be clear" - the amount
                  was shown, the reason never was. */}
              <dt className="text-zinc-500">
                Discount
                {typeof (invoice.metadata as Record<string, unknown> | null)?.discount_reason === "string"
                  && (invoice.metadata as Record<string, string>).discount_reason.trim() && (
                  <span className="ml-1.5 text-amber-300">
                    {(invoice.metadata as Record<string, string>).discount_reason}
                  </span>
                )}
              </dt>
              <dd className="tabular-nums text-amber-300">-{formatMoney(invoice.discount_cents, invoice.currency)}</dd>
            </div>
          )}
          {Number(invoice.tax_cents ?? 0) > 0 && (
            <div className="flex justify-between gap-3">
              <dt className="text-zinc-500">Tax</dt>
              <dd className="tabular-nums text-zinc-300">{formatMoney(invoice.tax_cents, invoice.currency)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3 border-t border-zinc-800 pt-1">
            <dt className="font-medium text-zinc-300">Total</dt>
            <dd className="font-semibold tabular-nums text-zinc-100">{formatMoney(invoice.total_cents, invoice.currency)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-zinc-500">Paid</dt>
            <dd className="tabular-nums text-emerald-300">{formatMoney(invoice.amount_paid_cents, invoice.currency)}</dd>
          </div>
          {method && (
            <div className="flex justify-between gap-3">
              <dt className="text-zinc-500">How</dt>
              <dd className="text-zinc-300">{PAID_BY[method] ?? method}</dd>
            </div>
          )}
        </dl>
      </div>
      <ModalFooter>
        {/* "how do I cancel open invoices that are errors - See Connor Boden"
            She reads bills on the client, so this is where the way out of
            one belongs. Money already taken is a refund, not this. */}
        {(invoice.status === "open" || invoice.status === "draft")
          && Number(invoice.amount_paid_cents ?? 0) === 0 && (
          <Button
            variant="ghost"
            className="mr-auto text-rose-300 hover:text-rose-200"
            loading={cancelling}
            onClick={async () => {
              const ok = await confirm({
                title: "Cancel this bill?",
                description: `${formatMoney(invoice.total_cents, invoice.currency)}. Nobody can pay it afterwards, and any payment link on it stops working.`,
                confirmLabel: "Cancel bill",
                danger: true,
              });
              if (!ok) return;
              setCancelling(true);
              try {
                await api.post(`/payments/invoices/${invoice.id}/void`, {});
                toast.success("Bill cancelled");
                onCancelled();
              } catch (err) {
                toast.error(err instanceof ApiError ? err.message : "That didn't work");
              } finally { setCancelling(false); }
            }}
          >
            Cancel bill
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button onClick={() => window.print()} leftIcon={<Printer className="h-3.5 w-3.5" />}>Print</Button>
      </ModalFooter>
    </Modal>
  );
}

function InvoiceCreateDialog({
  open, onClose, contactId, onCreated,
}: { open: boolean; onClose: () => void; contactId: string; onCreated: () => void }) {
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [lineItems, setLineItems] = useState<{ name: string; quantity: number; unit_amount_cents: number; product_id?: string | null }[]>([
    { name: "", quantity: 1, unit_amount_cents: 0 },
  ]);
  const [currency, setCurrency] = useState("USD");
  const [sendNow, setSendNow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get<{ products: Product[] }>("/payments/products").then((d) => setProducts(d.products)).catch(() => {});
      setLineItems([{ name: "", quantity: 1, unit_amount_cents: 0 }]);
      setSendNow(false);
    }
  }, [open]);

  const total = lineItems.reduce((s, li) => s + li.quantity * li.unit_amount_cents, 0);
  const updateLine = (i: number, p: Partial<typeof lineItems[number]>) => {
    setLineItems((prev) => prev.map((li, idx) => idx === i ? { ...li, ...p } : li));
  };
  const addLine = () => setLineItems((prev) => [...prev, { name: "", quantity: 1, unit_amount_cents: 0 }]);
  const removeLine = (i: number) => setLineItems((prev) => prev.filter((_, idx) => idx !== i));
  const useProduct = (i: number, productId: string) => {
    if (!productId) return updateLine(i, { product_id: null });
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateLine(i, { product_id: p.id, name: p.name, unit_amount_cents: p.amount_cents });
    setCurrency(p.currency);
  };

  const submit = async () => {
    if (lineItems.some((l) => !l.name.trim() || l.unit_amount_cents <= 0)) {
      toast.error("Each line item needs a name and amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/payments/invoices", { contact_id: contactId, currency, line_items: lineItems, send_now: sendNow });
      toast.success(sendNow ? "Invoice sent" : "Draft saved");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New invoice" size="lg">
      <div className="space-y-3">
        <div>
          <Label>Line items</Label>
          <ul className="space-y-2">
            {lineItems.map((li, i) => (
              <li key={i} className="grid grid-cols-[1fr_5rem_6rem_auto] gap-2 items-center">
                <div className="space-y-1">
                  <Input value={li.name} onChange={(e) => updateLine(i, { name: e.target.value })} placeholder="Description" className="h-8" />
                  {products.length > 0 && (
                    <Select value={li.product_id ?? ""} onChange={(e) => useProduct(i, e.target.value)} className="h-7 text-xs">
                      <option value="">— Or pick a product —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {formatMoney(p.amount_cents, p.currency)}</option>)}
                    </Select>
                  )}
                </div>
                <Input type="number" min={1} value={li.quantity} onChange={(e) => updateLine(i, { quantity: parseInt(e.target.value, 10) || 1 })} className="h-8" />
                <Input type="number" step="0.01" value={li.unit_amount_cents / 100} onChange={(e) => updateLine(i, { unit_amount_cents: Math.round(parseFloat(e.target.value || "0") * 100) })} className="h-8" />
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  disabled={lineItems.length <= 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300 disabled:opacity-30"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={addLine} className="mt-2">
            Add line item
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
          <span className="text-sm text-zinc-400">Total</span>
          <span className="text-base font-semibold text-zinc-100 tabular-nums">{formatMoney(total, currency)}</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input
            type="checkbox"
            checked={sendNow}
            onChange={(e) => setSendNow(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
          />
          Send invoice now (publishes through your payment provider)
        </label>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={lineItems.length === 0 || total === 0}>
          {sendNow ? "Send invoice" : "Save as draft"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Automations ──────────────────────────────────────────────────────────
/**
 * Two sections per contact: active runs (currently running or parked at a
 * wait step) and a per-automation summary of completed runs. The summary
 * collapses repeat runs into one row with a "× N" badge so a contact that
 * looped through "Welcome Drip" three times shows once instead of cluttering
 * the panel with three near-duplicate rows.
 */
export function AutomationsPanel({ contactId }: { contactId: string }) {
  const [data, setData] = useState<ContactAutomations | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<ContactAutomations>(`/contacts/${contactId}/automations`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ active: [], completed: [] }); });
    return () => { cancelled = true; };
  }, [contactId]);

  const total = (data?.active.length ?? 0) + (data?.completed.length ?? 0);
  const fromPath = `/contacts/${contactId}`;

  return (
    <Card icon={Zap} title="Automations" count={total || undefined}>
      {data === null ? (
        <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
      ) : total === 0 ? (
        <p className="px-4 py-3 text-xs text-zinc-500">No automations have run for this contact yet.</p>
      ) : (
        <div className="divide-y divide-zinc-800/40">
          {data.active.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Active</p>
              <ul>
                {data.active.map((a) => (
                  <li key={a.run_id}>
                    <Link
                      to={`/automations/${a.automation_id}`}
                      state={{ from: fromPath }}
                      className="flex items-start gap-3 px-4 py-2 hover:bg-zinc-800/40"
                    >
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                        {a.status === "waiting"
                          ? <Hourglass className="h-3.5 w-3.5 text-amber-300" />
                          : <Clock className="h-3.5 w-3.5 animate-pulse text-amber-300" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-zinc-200">{a.automation_name}</p>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {a.status === "waiting" && a.resume_at
                            ? <>Waiting{a.stage ? <> at <span className="font-mono">{a.stage}</span></> : null} · resumes {formatRelative(a.resume_at)}</>
                            : <>Running · started {formatRelative(a.started_at)}</>}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.completed.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Completed</p>
              <ul>
                {data.completed.map((c) => {
                  const tone = c.last_status === "succeeded" ? { bg: "bg-emerald-500/10", icon: "text-emerald-300", Icon: CheckCircle2 }
                    : c.last_status === "failed"             ? { bg: "bg-rose-500/10",    icon: "text-rose-300",    Icon: AlertCircle }
                    :                                          { bg: "bg-zinc-800",        icon: "text-zinc-400",    Icon: Clock };
                  const Icon = tone.Icon;
                  return (
                    <li key={c.automation_id}>
                      <Link
                        to={`/automations/${c.automation_id}`}
                        state={{ from: fromPath }}
                        className="flex items-start gap-3 px-4 py-2 hover:bg-zinc-800/40"
                      >
                        <div className={"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full " + tone.bg}>
                          <Icon className={"h-3.5 w-3.5 " + tone.icon} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="min-w-0 truncate text-sm text-zinc-200">{c.automation_name}</p>
                            {c.run_count > 1 && (
                              <Badge color="zinc" size="xs">× {c.run_count}</Badge>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-xs text-zinc-500">
                            {c.last_status === "failed" && c.last_error
                              ? <span className="text-rose-400">{c.last_error}</span>
                              : <>last run {formatRelative(c.last_run_at)}</>}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Do Not Disturb (per-channel consent) ─────────────────────────────────
/**
 * Per-channel opt-out controls. The master "Block all channels" toggle
 * sits on top — flipping it adds an `'all'` row to `contact_consent`
 * which short-circuits sends on every channel. The per-channel rows
 * underneath let the user block one channel without affecting others
 * (e.g. opt out of SMS but keep email allowed). Anything not listed
 * defaults to allowed.
 *
 * The runtime gate lives in `lib/messaging.ts::assertCanSend` — it
 * runs before every outbound message, so toggling here is the single
 * place that controls deliverability for a contact.
 */
const DND_CHANNELS: Array<{ key: string; label: string; icon: React.ComponentType<{ className?: string }>; has: (c: Contact) => boolean }> = [
  { key: "sms",       label: "SMS",         icon: MessageSquare, has: (c) => !!c.phone },
  { key: "email",     label: "Email",       icon: Mail,          has: (c) => !!c.email },
  // "Can we clean up the do not disturb and eliminate what's app and voice" —
  // she reaches people by text and email. `has: () => false` keeps these two
  // out of the list without losing them: `visibleChannels` still shows a
  // channel somebody is actually opted out of, so an old opt-out stays
  // visible and can still be turned back on.
  { key: "voice",     label: "Voice",       icon: Phone,         has: () => false },
  { key: "whatsapp",  label: "WhatsApp",    icon: MessageSquare, has: () => false },
  { key: "instagram", label: "Instagram",   icon: MessageSquare, has: (c) => !!c.socials?.instagram },
  { key: "facebook",  label: "Facebook",    icon: MessageSquare, has: (c) => !!c.socials?.facebook },
  { key: "linkedin",  label: "LinkedIn",    icon: MessageSquare, has: (c) => !!c.socials?.linkedin },
  { key: "twitter",   label: "Twitter / X", icon: MessageSquare, has: (c) => !!c.socials?.twitter },
  { key: "telegram",  label: "Telegram",    icon: MessageSquare, has: (c) => !!c.socials?.telegram },
  { key: "tiktok",    label: "TikTok",      icon: MessageSquare, has: (c) => !!c.socials?.tiktok },
];

export function ConsentPanel({ contact }: { contact: Contact }) {
  const contactId = contact.id;
  const toast = useToast();
  const [rows, setRows] = useState<ContactConsentRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = async () => {
    try {
      const d = await api.get<{ consent: ContactConsentRow[] }>(`/contacts/${contactId}/consent`);
      setRows(d.consent);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { void reload(); }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (channel: string, current: ContactConsentRow | undefined) => {
    setBusy(channel);
    const nextStatus: "opted_in" | "opted_out" = current?.status === "opted_out" ? "opted_in" : "opted_out";
    try {
      await api.put(`/contacts/${contactId}/consent`, { channel, status: nextStatus, source: "user" });
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setBusy(null);
    }
  };

  const lookup = new Map((rows ?? []).map((r) => [r.channel, r]));
  const masterRow = lookup.get("all");
  const masterBlocked = masterRow?.status === "opted_out";
  const blockedCount = (rows ?? []).filter((r) => r.status === "opted_out").length;
  // Hide channels the contact doesn't have an identity for — but keep
  // showing any channel that's already blocked so the user can see and
  // un-block it even if the underlying identity has been removed.
  const visibleChannels = DND_CHANNELS.filter((c) => c.has(contact) || lookup.get(c.key)?.status === "opted_out");

  return (
    <Card
      icon={masterBlocked ? ShieldAlert : ShieldCheck}
      title="Do not disturb"
      count={blockedCount || undefined}
    >
      {rows === null ? (
        <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
      ) : (
        <div>
          {/* Master kill */}
          <button
            type="button"
            onClick={() => toggle("all", masterRow)}
            disabled={busy === "all"}
            className={cn(
              "flex w-full items-center justify-between gap-3 px-4 py-3 transition",
              masterBlocked ? "bg-rose-500/10 hover:bg-rose-500/20" : "hover:bg-zinc-800/40",
            )}
          >
            <div className="min-w-0 flex-1 text-left">
              <p className={cn("text-sm font-medium", masterBlocked ? "text-rose-200" : "text-zinc-200")}>
                Block all channels
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                {masterBlocked
                  ? (masterRow?.reason ? `Blocked: ${masterRow.reason}` : "Blocked — no outbound will deliver")
                  : "Master kill switch — overrides every channel"}
              </p>
            </div>
            <ToggleDot active={masterBlocked} />
          </button>

          <div className="border-t border-zinc-800/40" />

          {/* Per-channel */}
          <ul className="divide-y divide-zinc-800/40">
            {visibleChannels.map((c) => {
              const row = lookup.get(c.key);
              const blocked = row?.status === "opted_out";
              const Icon = c.icon;
              const disabled = masterBlocked || busy === c.key;
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    onClick={() => !disabled && toggle(c.key, row)}
                    disabled={disabled}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left transition",
                      disabled && "opacity-50",
                      !disabled && (blocked ? "bg-rose-500/5 hover:bg-rose-500/10" : "hover:bg-zinc-800/40"),
                    )}
                  >
                    <Icon className={cn("h-3.5 w-3.5 shrink-0", blocked ? "text-rose-400" : "text-zinc-500")} />
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-sm", blocked ? "text-rose-200" : "text-zinc-200")}>{c.label}</p>
                      {row?.reason && (
                        <p className="mt-0.5 truncate text-[10px] text-zinc-500">{row.reason}</p>
                      )}
                    </div>
                    <ToggleDot active={blocked} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ToggleDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        active ? "bg-rose-500/40" : "bg-zinc-700",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 rounded-full bg-zinc-100 transition-transform",
          active ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </span>
  );
}

// ─── Notes ────────────────────────────────────────────────────────────────
const NOTES_PAGE_SIZE = 25;

export function NotesPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [total, setTotal] = useState(0);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setNotes(null);
    api.get<{ notes: Note[]; total?: number }>("/notes", { contact_id: contactId, limit: NOTES_PAGE_SIZE })
      .then((d) => { if (!cancelled) { setNotes(d.notes); setTotal(d.total ?? d.notes.length); } })
      .catch(() => { if (!cancelled) { setNotes([]); setTotal(0); } });
    return () => { cancelled = true; };
  }, [contactId]);

  const loadMore = async () => {
    if (!notes || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await api.get<{ notes: Note[]; total?: number }>("/notes", {
        contact_id: contactId, limit: NOTES_PAGE_SIZE, offset: notes.length,
      });
      // Filter out any optimistic-temp IDs we might have re-fetched against —
      // the backend doesn't return tmp_* ids so this is a no-op in practice
      // but keeps types stable if the contract ever shifts.
      setNotes((prev) => prev ? [...prev, ...d.notes] : d.notes);
      if (typeof d.total === "number") setTotal(d.total);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load more notes");
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    const optimistic: Note = {
      id: `tmp_${Date.now()}`,
      contact_id: contactId,
      opportunity_id: null,
      body: body.trim(),
      pinned: false,
      author_id: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setNotes((prev) => prev ? [optimistic, ...prev] : [optimistic]);
    setTotal((t) => t + 1);
    const sent = body;
    setBody("");
    try {
      const res = await api.post<{ note: Note }>("/notes", { contact_id: contactId, body: sent });
      setNotes((prev) => prev ? prev.map((n) => n.id === optimistic.id ? res.note : n) : prev);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save note");
      setNotes((prev) => prev ? prev.filter((n) => n.id !== optimistic.id) : prev);
      setTotal((t) => Math.max(0, t - 1));
      setBody(sent);
    } finally {
      setSaving(false);
    }
  };

  const hasMore = !!notes && notes.length < total;

  return (
    <Card icon={StickyNote} title="Notes">
      <div className="p-3 space-y-2">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note…" className="min-h-12 text-xs" />
        <Button size="sm" onClick={submit} disabled={!body.trim() || saving} className="w-full">
          Save note
        </Button>
      </div>
      {notes && notes.length > 0 && (
        <>
          <ul className="divide-y divide-zinc-800/40 max-h-72 overflow-y-auto">
            {notes.map((n) => (
              <li key={n.id} className="px-4 py-2.5">
                {(n.author_name || n.author_credential) && (
                  <p className="text-[11px] font-medium text-indigo-300">
                    {[n.author_name, n.author_credential].filter(Boolean).join(" ")}:
                  </p>
                )}
                <p className="text-xs text-zinc-200 whitespace-pre-wrap">{n.body}</p>
                <p className="text-[10px] text-zinc-500 mt-1">{formatRelative(n.created_at)}</p>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className="border-t border-zinc-800/40 px-3 py-2">
              <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore} className="w-full">
                {loadingMore ? "Loading…" : `Load more (${total - notes.length} remaining)`}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Tags ─────────────────────────────────────────────────────────────────
/**
 * Tag picker. Renders the contact's current tags + a typeable suggestion
 * box that shows each existing tag in its color (single click adds it).
 * Pressing Enter on a non-matching string creates a brand-new tag.
 *
 * `contact` is passed in (rather than a contact id) because we need the
 * already-resolved tag list on the contact to render the chips. After
 * any mutation the caller is notified via `onContactChange` so the
 * contact-detail header / inbox info panel can stay in sync.
 */
export function TagsPanel({
  contact, onContactChange, compact = false,
}: {
  contact: Contact;
  onContactChange?: (next: Contact) => void;
  /** In the left rail beside the identity fields: a tighter box. */
  compact?: boolean;
}) {
  const toast = useToast();
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ tags: Tag[] }>("/tags")
      .then((d) => { if (!cancelled) setAllTags(d.tags); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contact.id]);

  // Click-outside / Esc closes the picker.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const addTag = async (name: string) => {
    if (!name.trim()) return;
    const trimmed = name.trim();
    if (contact.tags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      setInput(""); setOpen(false); return;
    }
    const optimistic = { id: `tmp_${Date.now()}`, name: trimmed, color: "indigo" };
    onContactChange?.({ ...contact, tags: [...contact.tags, optimistic] });
    setInput(""); setOpen(false);
    try {
      await api.post(`/contacts/${contact.id}/tags`, { tag: trimmed });
      // Re-fetch the contact to pick up the real tag id + color.
      const r = await api.get<{ contact: Contact }>(`/contacts/${contact.id}`);
      onContactChange?.(r.contact);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      onContactChange?.(contact);
    }
  };
  const removeTag = async (name: string) => {
    const before = contact.tags;
    onContactChange?.({ ...contact, tags: contact.tags.filter((t) => t.name !== name) });
    try {
      await api.delete(`/contacts/${contact.id}/tags/${encodeURIComponent(name)}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      onContactChange?.({ ...contact, tags: before });
    }
  };

  const attached = new Set(contact.tags.map((t) => t.name.toLowerCase()));
  const needle = input.trim().toLowerCase();
  const suggestions = allTags
    .filter((t) => !attached.has(t.name.toLowerCase()))
    .filter((t) => !needle || t.name.toLowerCase().includes(needle))
    .slice(0, 12);
  const exactMatch = needle && allTags.some((t) => t.name.toLowerCase() === needle);

  return (
    <Card icon={TagIcon} title="Tags">
      <div className={compact ? "space-y-1.5 p-2" : "space-y-2 p-3"}>
        <div className="flex flex-wrap gap-1.5">
          {contact.tags.length === 0 && <span className="text-xs text-zinc-600">No tags yet</span>}
          {contact.tags.map((t) => (
            <Badge key={t.id} color={t.color} size="sm" onRemove={() => removeTag(t.name)}>{t.name}</Badge>
          ))}
        </div>
        <div ref={wrapRef} className="relative">
          <Input
            value={input}
            onChange={(e) => { setInput(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input.trim()) {
                e.preventDefault();
                void addTag(input.trim());
              }
            }}
            placeholder="Add a tag…"
            leftIcon={<TagIcon className="h-3.5 w-3.5" />}
            className="h-8 text-xs"
          />
          {open && (suggestions.length > 0 || (needle && !exactMatch)) && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40 animate-fade-in">
              {suggestions.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => void addTag(t.name)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-zinc-900"
                >
                  <Badge color={t.color} size="xs">{t.name}</Badge>
                  {t.usage_count != null && (
                    <span className="ml-auto text-[10px] text-zinc-600">{t.usage_count}</span>
                  )}
                </button>
              ))}
              {needle && !exactMatch && (
                <button
                  type="button"
                  onClick={() => void addTag(input.trim())}
                  className="flex w-full items-center gap-2 border-t border-zinc-800/60 px-2 py-1.5 text-left text-xs text-zinc-300 hover:bg-zinc-900"
                >
                  <Plus className="h-3 w-3 text-zinc-500" />
                  Create new tag <span className="font-medium text-zinc-100">"{input.trim()}"</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Layout primitives ────────────────────────────────────────────────────
/**
 * Standard panel chrome — header with icon + title + optional count or
 * action button, then a body slot. Kept minimal so the panels above stay
 * focused on data shapes, not chrome.
 */
/** What happened to a visit, in a word. */
function VisitStatus({ status }: { status?: string | null }) {
  if (!status) return null;
  const look: Record<string, { label: string; cls: string }> = {
    completed: { label: "Done", cls: "bg-emerald-500/10 text-emerald-300" },
    arrived:   { label: "Arrived", cls: "bg-emerald-500/10 text-emerald-300" },
    confirmed: { label: "Confirmed", cls: "bg-sky-500/10 text-sky-300" },
    scheduled: { label: "Booked", cls: "bg-sky-500/10 text-sky-300" },
    cancelled: { label: "Cancelled", cls: "bg-zinc-700/40 text-zinc-400" },
    no_show:   { label: "No show", cls: "bg-rose-500/10 text-rose-300" },
  };
  const l = look[status];
  if (!l) return null;
  return <span className={cn("rounded px-1 text-[10px]", l.cls)}>{l.label}</span>;
}

function Card({
  icon: Icon, title, count, action, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <div className="flex items-center justify-between border-b border-zinc-800/70 px-4 py-3">
        {/* Same heading as the medspa panels — larger, bold, blue, because
            she asked for it on Labs and for it to carry through. */}
        <h3 className="flex items-center gap-2 text-sm font-bold tracking-wide text-sky-300">
          <Icon className="h-4 w-4" /> {title}
        </h3>
        {action ?? (count != null && <span className="text-[10px] text-zinc-500">{count}</span>)}
      </div>
      {children}
    </div>
  );
}

function PlusButton({ onClick, aria }: { onClick: () => void; aria: string }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
      aria-label={aria}
    >
      <Plus className="h-3.5 w-3.5" />
    </button>
  );
}

// Avoid unused-import warning when consumers want to render the trash icon
// alongside their own panels — re-export so they don't double-import.
export { Trash2 };

// ─── Order history ────────────────────────────────────────────────────────
/**
 * "It has appointment history and order history. Order history is the
 * receipts for all purchases services and products."
 *
 * One row per ticket, newest first, with what was on it. The Payments panel
 * answers "what do they owe"; this answers "what did they buy", which is a
 * different question and the one she asks at the desk.
 */
/**
 * What she sold them off the shelf.
 *
 * "please see product history this is only product history just to show what
 *  we gave them if it is combined with a service the invoice can show both
 *  over in the wallet under payments we can view the invoice that will have
 *  product and services together"
 *
 * It used to list every line on every ticket, so Richard's Weight Loss
 * initial Consultation and his Tirzepatide package both turned up under
 * Product history, which is neither. With `kinds` set it keeps only those
 * lines, and a ticket with none of them drops out altogether. The whole
 * ticket is still one click away in the wallet.
 */
export function OrderHistoryPanel({
  contactId, max = 30, title = "Order history", kinds,
}: { contactId: string; max?: number; title?: string; kinds?: string[] }) {
  const { data: invoices, error, reload } = usePanelData(
    () => api.get<{ invoices: Invoice[] }>("/payments/invoices", { contact_id: contactId, limit: max })
      .then((d) => d.invoices
        .filter((i) => i.status !== "draft" && i.status !== "void")
        .map((i) => kinds
          ? { ...i, line_items: (i.line_items ?? []).filter((li) => kinds.includes(li.kind ?? "product")) }
          : i)
        .filter((i) => !kinds || i.line_items.length > 0)),
    [contactId, max, kinds?.join(",")],
  );

  if (error) {
    return (
      <Card icon={Receipt} title={title}>
        <PanelErrorRow message={error} onRetry={reload} />
      </Card>
    );
  }
  if (!invoices) return null;
  if (invoices.length === 0) {
    return (
      <Card icon={Receipt} title={title}>
        <p className="px-4 py-3 text-sm text-zinc-500">Nothing bought yet.</p>
      </Card>
    );
  }

  return (
    <Card icon={Receipt} title={title}>
      <ul className="divide-y divide-zinc-800/40">
        {invoices.map((inv) => (
          <li key={inv.id} data-order-row className="px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium text-zinc-200">
                {inv.number ?? "Ticket"}
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  {formatDateTime(inv.paid_at ?? inv.created_at)}
                </span>
              </p>
              <div className="flex shrink-0 items-center gap-2">
                {/* When the panel is filtered, the ticket total would be the
                    wrong number beside a subset of its lines. */}
                <span className="text-sm tabular-nums text-zinc-100">
                  {formatMoney(kinds
                    ? inv.line_items.reduce((t, li) => t + li.unit_amount_cents * (li.qty ?? li.quantity ?? 1), 0)
                    : inv.total_cents)}
                </span>
                <Badge color={inv.status === "paid" ? "emerald" : "amber"} size="xs">
                  {inv.status === "paid" ? "Paid" : inv.status}
                </Badge>
              </div>
            </div>
            {inv.line_items.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {inv.line_items.map((li, i) => (
                  <li key={i} data-order-line className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="truncate text-zinc-400">
                      {(li.qty ?? li.quantity ?? 1) > 1 && (
                        <span className="text-zinc-500">{li.qty ?? li.quantity} × </span>
                      )}
                      {li.name}
                    </span>
                    <span className="shrink-0 tabular-nums text-zinc-500">
                      {formatMoney(li.unit_amount_cents * (li.qty ?? li.quantity ?? 1))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
